import Preact from 'preact';
import * as S from '../data/summary';
import * as Hooks from 'preact/hooks';
import Box from '@mui/material/Box';
import { Price } from '@dora/backpack/';
import * as AR from '@dora/arctic';
import * as Scene from './configuration/babylonScene';
import * as Style from './configuration/style';
import { Cart, CheckoutLocation } from './hooks/useParentAPI';
import * as NavigationActions from '../store/actions/navigation';
import * as ConfigurationActions from '../store/actions/configuration';
import * as Selectors from '../store/selectors';
import { useDispatch, useSelector } from './hooks/redux';
import { getParticleIdsInTree } from './configuration/furniture';
import { useAsync } from './hooks/useAsync';
import { useLatest } from './hooks/useLatest';
import { useTheme } from '@mui/material/styles';
import * as ArView from './ar/arView';
import * as ArRunway from './ar/arRunway';
import * as O from 'fp-ts/Option';
import {
    ConfiguratorDefinition,
    fetchConfiguratorDefinition,
} from './configuration/configurationDefinition';
import {
    TopBarWithButtons,
    ConfigAlert,
    StartupScreen,
    ConfigCheckoutDrawer,
    SaveShareDrawer,
    ConfigSceneLoading,
} from '@dora/components';
import { appBarHeight, Platform } from '@dora/components/src/sizing';
import { TranslateContext } from '@denysvuika/preact-translate';
import { useAppControl } from './hooks/useAppControl';
import * as CoreStore from '../store/core';
import { AppError } from '../data/appError';
import {
    getPropertyOptions,
    isChoiceCompatible,
    resolveSelectionProposals,
} from './configuration/compatibility';
import { PropertyValue, PropertySelections } from './configuration/types';
import { useCachingGetBasketforParticles } from './configuration/caching';
import { useAssembleFurniture } from './configuration/useAssembleFurniture';
import { getPrice } from './configuration/basket';
import { usePriceDeltas } from './configuration/usePriceDeltas';
import { useConfiguratorSelectionState } from './configuration/useConfiguratorSelectionState';
import * as ExitScreen from './exitModal';
import { useLoadProductData } from './hooks/useGetProductData';
import { useLoadConfiguration } from './hooks/useInitialConfigurationDocument';
import { ConfigurationPostData, configurationsEqual, generate } from '../data/shareConfiguration';
import * as U from '../data/url';
import { pipe } from 'fp-ts/lib/function';
import { useSaveConfigurationLinkState } from './hooks/useSaveLinkState';
import { useOpenable } from './hooks/useOpenable';
import { ModelRequest } from './ar';
import { useAddCommand } from './hooks/useConsoleCommand';
import { Product } from '../data/product';
import * as Pr from '../data/product';
import { getLeadTime } from '../data/leadTime';

export function Component(props: {
    proceedToCart: (cart: Cart, from: CheckoutLocation) => void;
    platform: Platform;
    portrait: boolean;
    copyToClipboard: (url: string) => void;
    toggleModal: (modal: boolean) => void;
    disableDataErrorHandling: boolean;
}) {
    const gavelUrl = useSelector(Selectors.Core.configurationServiceUrl);
    const modelsUrl = useSelector(Selectors.Core.modelsUrl);
    const tenantId = useSelector(Selectors.Core.tenantIdentifier);
    const conversionId = useSelector(Selectors.ConversionId.conversionId);
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const launchData = useSelector(Selectors.Core.configurationLaunchData);
    const arCompatibility = useSelector(Selectors.Core.arCompatibility);

    const configuratorId = useSelector(Selectors.InitialConfiguration.configuratorId);
    const startingSelections = useSelector(Selectors.InitialConfiguration.startingSelections);
    const startingAttachments = useSelector(Selectors.InitialConfiguration.startingAttachments);

    const [snapshot, setSnapshot] = Hooks.useState<string>();
    const [snapshotFn, setSnapshotFn] = Hooks.useState<() => Promise<string>>();

    const { t } = Hooks.useContext(TranslateContext);
    const theme = useTheme();
    const dispatch = useDispatch();

    // The model generation methods for use in the AR runway component, set by the child babylonScene component
    const [generateGltfFn, setGenerateGltfFn] = Hooks.useState<() => O.Option<Promise<Blob>>>(
        () => () => O.none,
    );
    const [generateModelrequestFn, setGenerateModelRequestFn] = Hooks.useState<
        () => O.Option<ModelRequest>
    >(() => () => O.none);

    const arRunway = useOpenable();

    Hooks.useEffect(() => {
        if (launchData?.ar === true) {
            arRunway.open();
        }
    }, [launchData?.ar]);

    useLoadConfiguration();

    const configuratorDefinition: ConfiguratorDefinition | undefined = useAsync(() => {
        if (configuratorId) {
            return fetchConfiguratorDefinition(configuratorId, tenantId);
        }
        return Promise.resolve(undefined);
    }, [configuratorId]).result;

    const spaceId = configuratorDefinition?.configurationSpaceId;

    const appControl = useAppControl();
    Hooks.useEffect(() => {
        appControl.enhanceCloseApp('configuration_reset', () => {
            setActiveView('default');
        });
    }, [appControl]);

    useLoadProductData();
    const getProduct = useSelector(Selectors.ProductData.productBySkuFn);

    const existingPropertyOptions = useAsync(
        async () =>
            spaceId && gavelUrl && tenantId
                ? getPropertyOptions(gavelUrl, tenantId, spaceId, {})
                : undefined,
        [gavelUrl, tenantId, spaceId],
    ).result;

    const fallbacks = Hooks.useMemo(
        () =>
            existingPropertyOptions
                ? [
                      ...Object.entries(configuratorDefinition?.propertyPriorities ?? {}).flatMap(
                          ([property, values]) =>
                              values.map((value): [string, PropertyValue] => [property, value]),
                      ),
                      ...(existingPropertyOptions
                          ? Object.entries(existingPropertyOptions)
                                .flatMap(([property, values]) =>
                                    values.map((value): [string, PropertyValue] => [
                                        property,
                                        value,
                                    ]),
                                )
                                .filter(
                                    ([property, _]) =>
                                        configuratorDefinition?.configurableProperties[property],
                                )
                          : []),
                  ].map(([property, value]) => ({ [property]: value }))
                : undefined,
        [existingPropertyOptions, configuratorDefinition?.propertyPriorities],
    );

    const initialSelections = useAsync(async () => {
        if (!startingSelections || !spaceId || !fallbacks || !gavelUrl || !tenantId) {
            return;
        }

        const selections = (
            await resolveSelectionProposals(gavelUrl, tenantId, spaceId, [
                startingSelections,
                ...fallbacks,
            ])
        ).selections;
        return selections;
    }, [gavelUrl, tenantId, spaceId, startingSelections, fallbacks]).result;

    const fixedSelections = Hooks.useMemo(() => ({}), []);

    const {
        propertySelections,
        resolveSelection,
        attachmentProposals,
        selectAttachment,
        selectProperty,
        compatiblePropertyOptions,
        undo,
        redo,
        changes,
    } = useConfiguratorSelectionState(
        spaceId,
        initialSelections,
        startingAttachments,
        fixedSelections,
        fallbacks,
    );

    // controls the loading overlay on the scene and when the change alert is displayed
    const [modelLoading, setModelLoading] = Hooks.useState(false);
    // the scene is ready for a snapshot when all model loading is finished for all queued updates
    const [sceneReadyForSnapshot, setSceneReadyForSnapshot] = Hooks.useState(true);
    const [changesIncurred, setChangesIncurred] = Hooks.useState(false);
    const [showAlert, setShowAlert] = Hooks.useState(false);
    const [sceneError, setSceneError] = Hooks.useState(false);
    const [activeView, setActiveView] = Hooks.useState<
        'default' | 'checkout' | 'exit' | 'save' | 'ar'
    >('default');

    Hooks.useEffect(() => {
        if (propertySelections === undefined) {
            setModelLoading(true);
        }
    }, [propertySelections]);

    Hooks.useEffect(() => {
        if (Object.keys(changes).length > 0 && showAlert === false) {
            setChangesIncurred(true);
        }
    }, [changes]);

    Hooks.useEffect(() => {
        if (!modelLoading && changesIncurred) {
            setShowAlert(true);
            setChangesIncurred(false);
        }
    }, [modelLoading]);

    let modelTenantUrl = `${modelsUrl}/v1/${tenantId}`;
    if (tenantId === 'victorianplumbing') {
        modelTenantUrl = `${modelsUrl}/v2/${tenantId}`;
    }

    const { furnitureTree, attachmentSelections, resolveFurnitureChoiceTree } =
        useAssembleFurniture(
            modelTenantUrl,
            spaceId,
            propertySelections,
            attachmentProposals,
            dispatch,
            props.disableDataErrorHandling,
        );

    const { getBasketForParticles } = useCachingGetBasketforParticles(
        dispatch,
        props.disableDataErrorHandling,
    );

    const getPriceCached = Hooks.useCallback(
        (selections: PropertySelections, attachmentProposals: Record<string, string>) =>
            getPrice(
                resolveFurnitureChoiceTree,
                getBasketForParticles,
                selections,
                attachmentProposals,
            ),
        [resolveFurnitureChoiceTree, getBasketForParticles],
    );

    const furnitureParticles = Hooks.useMemo(
        () => furnitureTree && getParticleIdsInTree(furnitureTree),
        [furnitureTree],
    );

    const locale = useSelector(Selectors.Core.locale);
    const currency = useSelector(Selectors.Core.currency);

    const [enableBasketDebug, setEnableBasketDefault] = Hooks.useState(false);

    useAddCommand(
        'enableBasketDebug',
        () => {
            setEnableBasketDefault(true);
        },
        [furnitureParticles],
    );

    const basket = useAsync(async () => {
        if (!furnitureParticles) return;
        return await getBasketForParticles(furnitureParticles);
    }, [furnitureParticles, getBasketForParticles]).result;

    const [configPost, setConfigPost] = Hooks.useState<ConfigurationPostData>();

    const { sharingUrlRequest, generateNewSharingUrl } = useSaveConfigurationLinkState(
        () => {
            if (configPost) {
                return configPost;
            }
        },
        currentUrl,
        activeView === 'ar',
    );

    Hooks.useEffect(() => {
        if (
            (activeView === 'save' || activeView === 'exit' || activeView === 'ar') &&
            propertySelections &&
            configuratorId
        ) {
            const postData = generate(propertySelections, attachmentSelections, configuratorId);
            if (!configPost || !configurationsEqual(configPost, postData)) {
                setConfigPost(postData);
            }
        }
    }, [activeView, propertySelections, attachmentSelections, configuratorId]);

    Hooks.useEffect(() => {
        generateNewSharingUrl();
    }, [configPost]);

    const qrUrl = sharingUrlRequest?.result
        ? pipe(sharingUrlRequest.result, U.url, U.fromQrCode, U.toString)
        : undefined;

    const basketItems = Hooks.useMemo(() => {
        if (getProduct && basket) {
            const products = basket.products.reduce((a, p) => {
                const product = getProduct(p.productId);
                if (product) {
                    for (let i = 0; i < p.quantity; i++) {
                        a.push(product);
                    }
                } else {
                    if (!props.disableDataErrorHandling) {
                        dispatch(CoreStore.error(AppError.CONFIGURATION_DATA_LOAD_FAILED));
                        dispatch(
                            ConfigurationActions.errorAction({
                                productId: p.productId,
                                reason: 'missing product data',
                                type: 'catch all',
                            }),
                        );
                    }
                }
                return a;
            }, [] as Array<Product>);

            const originalTotal = products.reduce(
                (a, p) => (p.originalPrice ? a + p.originalPrice : a + p.price),
                0,
            );

            const leadTimeDescription = getLeadTime(products.map(p => p.leadTime));
            const newBasket = products.map(p => ({
                productId: Pr.productId(p),
                productName: Pr.summaryName(p),
                productPrice: Price.toLocaleString(p.price, locale, currency),
                originalPrice: p.originalPrice
                    ? Price.toLocaleString(p.originalPrice, locale, currency)
                    : undefined,
            }));

            if (enableBasketDebug) {
                // Basket debug bypasses logging framework as it's controlled by a dedicated flag
                console.group('New basket');
                console.group('Particle Ids:');
                furnitureParticles?.forEach(p => console.log(p));
                console.groupEnd();
                console.group('Basket Lines:');
                console.table(newBasket);
                console.log('Total Price: ' + basket.price);
                console.groupEnd();
                console.groupEnd();
            }

            const discount = originalTotal - basket.price;
            const percent = (discount / originalTotal) * 100;
            const savingsText = t('i18n.global.basketSavingsText', {
                percent: percent.toFixed(0),
                discount: Price.toLocaleString(discount, locale, currency),
            });
            const originalTotalText = Price.toLocaleString(originalTotal, locale, currency);
            const savingsActive = originalTotal > basket.price;

            return {
                products: newBasket,
                originalTotal: savingsActive ? originalTotalText : undefined,
                savings: savingsActive ? savingsText : undefined,
                leadTimeDescription,
            };
        }
    }, [getProduct, basket]);

    const { priceDeltas, requestPriceDelta } = usePriceDeltas(
        getPriceCached,
        basket?.price,
        resolveSelection,
        attachmentProposals,
    );

    const latestValidBasket = useLatest(basket, v => v !== undefined);

    const onUpdateSelection = (propertyName: string, propertyValue: PropertyValue) => {
        const [category, type] = propertyName.split(':');
        dispatch(
            ConfigurationActions.selectPropertyAction({
                category,
                type,
                newOption: propertyValue?.toString() ?? '<None>',
                previousOption: propertySelections?.[propertyName]?.toString() ?? '<None>',
                choiceIsCompatible: isChoiceCompatible(
                    compatiblePropertyOptions,
                    propertyName,
                    propertyValue,
                ),
            }),
        );
        selectProperty(propertyName, propertyValue);
        setShowAlert(false);
    };

    const addToCart = Hooks.useCallback(
        (from: CheckoutLocation) => {
            if (conversionId && basket) {
                const { products, price } = basket;
                appControl.closeApp();
                props.proceedToCart(
                    {
                        conversionId,
                        products: products.map(
                            ({ productId, quantity }: { productId: string; quantity: number }) => ({
                                id: productId,
                                quantity,
                            }),
                        ),
                        price,
                    },
                    from,
                );
            }
        },
        [conversionId, basket],
    );

    const enterAr =
        arCompatibility === AR.Compatibility.IncompatibleBrowser
            ? undefined
            : () => {
                  dispatch(NavigationActions.viewInArAction({ mode: 'advanced' }));
                  if (arCompatibility === AR.Compatibility.Compatible) {
                      setActiveView('default');
                      arRunway.open();
                      props.toggleModal(true);
                  } else if (arCompatibility === AR.Compatibility.IncompatibleDesktop) {
                      setActiveView('ar');
                  }
              };

    Hooks.useEffect(() => {
        if (snapshotFn && sceneReadyForSnapshot && !sceneError && activeView === 'checkout') {
            snapshotFn().then((snapshot: string) => setSnapshot(snapshot));
        }
    }, [sceneReadyForSnapshot, activeView, snapshotFn, sceneError]);

    Hooks.useEffect(() => {
        switch (activeView) {
            case 'checkout':
                dispatch(
                    NavigationActions.finaliseAction({
                        products: latestValidBasket?.products ?? [],
                        value: latestValidBasket?.price ?? 0,
                    }),
                );
                break;
            case 'exit':
                dispatch(NavigationActions.exitViewShownAction());
                break;
            case 'save':
                dispatch(ConfigurationActions.shareAction());
        }
    }, [activeView]);

    return initialSelections && configuratorDefinition ? (
        <>
            {arRunway.isOpen && (
                <ArRunway.Component
                    onReturn={() => {
                        arRunway.close();
                        props.toggleModal(false);
                    }}
                    getModelRequest={generateModelrequestFn}
                    getGltfBlob={generateGltfFn}
                    returnToPlanner={t('i18n.configuration.returnToPlanner')}
                    placement="wall"
                />
            )}
            <Box
                sx={{
                    // Row 0 is for the app bar
                    // Row 1 is for the scene and control panel
                    gridTemplateRows: `${appBarHeight}px 1fr`,
                    display: 'grid',
                    width: '100%',
                    height: '100%',
                    visibility: arRunway.isOpen ? 'hidden' : 'visible',
                }}
            >
                <TopBarWithButtons.Component
                    onArClick={enterAr}
                    onBasketClick={() => {
                        setActiveView('checkout');
                    }}
                    onCloseClick={() => {
                        setActiveView('exit');
                        setShowAlert(false);
                    }}
                    onSaveClick={() => setActiveView('save')}
                    total={Price.toLocaleString(latestValidBasket?.price ?? 0, locale, currency)}
                    disabled={!propertySelections}
                />

                <ExitScreen.Component
                    open={activeView === 'exit'}
                    onCancel={() => {
                        setActiveView('default');
                    }}
                    size={props.platform === Platform.DESKTOP ? 'large' : 'medium'}
                    copyToClipboard={text => {
                        dispatch(NavigationActions.shareLinkCopiedAction());
                        props.copyToClipboard(text);
                    }}
                    sharingUrlRequest={sharingUrlRequest}
                    primaryCtaText={t('i18n.configuration.exitPrimaryCta')}
                    secondaryCtaText={t('i18n.global.exitSecondaryCta')}
                    titleText={t('i18n.configuration.exitTitle')}
                    linkError={t('i18n.global.linkError')}
                    linkHelperText={t('i18n.global.linkHelper')}
                    linkLoading={t('i18n.global.linkLoading')}
                    linkCtaText={t('i18n.global.linkCta')}
                />
                <SaveShareDrawer.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={activeView === 'save'}
                    onClose={() => setActiveView('default')}
                    sharingUrlResult={sharingUrlRequest.result}
                    sharingUrlError={sharingUrlRequest.error}
                    sharingUrlRetry={sharingUrlRequest.retry}
                    copyToClipboard={text => {
                        dispatch(NavigationActions.shareLinkCopiedAction());
                        props.copyToClipboard(text);
                    }}
                    qrUrl={qrUrl}
                    linkHelperText={t('i18n.global.linkHelper')}
                    linkCta={t('i18n.global.linkCta')}
                    linkErrorHeading={t('i18n.global.unknownErrorHeading')}
                    linkErrorBody={t('i18n.global.linkError')}
                    linkLoading={t('i18n.global.linkLoading')}
                    shareHeading={t('i18n.configuration.shareHeading')}
                    sharePrompt={t('i18n.configuration.shareHelpText')}
                    title={t('i18n.global.shareDrawerHeader')}
                    mailtoBody={t('i18n.configuration.mailtoBody')}
                    mailtoSubject={t('i18n.configuration.mailtoSubject')}
                    qrTab={t('i18n.global.qrTabHeader')}
                    urlTab={t('i18n.global.urlTabHeader')}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                />
                <ArView.Component
                    open={activeView === 'ar'}
                    platform={props.platform}
                    portrait={props.portrait}
                    onClose={() => setActiveView('default')}
                    sharingUrlRequest={sharingUrlRequest}
                    img={'assets/ARiPhone.png'}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                />
                <ConfigCheckoutDrawer.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={activeView === 'checkout'}
                    onClose={() => {
                        setActiveView('default');
                        setSnapshot(undefined);
                    }}
                    title={t('i18n.global.finalise')}
                    items={basketItems ? basketItems.products : []}
                    itemsHeader={t('i18n.configuration.checkoutTitle')}
                    leadTime={basketItems?.leadTimeDescription ?? ''}
                    totalPriceText={t('i18n.global.totalPrice')}
                    totalPrice={Price.toLocaleString(
                        latestValidBasket?.price ?? 0,
                        locale,
                        currency,
                    )}
                    savings={basketItems?.savings}
                    totalPriceBeforeDiscount={basketItems?.originalTotal}
                    onFinaliseClick={() => addToCart('finalise step')}
                    onArClick={enterAr}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                    saveText={t('i18n.global.saveText')}
                    onSaveClick={() => setActiveView('save')}
                    primaryCta={t('i18n.global.checkoutPrimaryCta')}
                    secondaryCta={t('i18n.global.checkoutSecondaryCta')}
                    snapshot={snapshot}
                    loading={!sceneReadyForSnapshot}
                    error={sceneError}
                />
                <Box
                    sx={{
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <ConfigAlert.Component
                            body={
                                Object.keys(changes).length > 1
                                    ? t('i18n.configuration.changeAlert.many', {
                                          numberOfChanges: Object.keys(changes).length,
                                      })
                                    : t('i18n.configuration.changeAlert.one')
                            }
                            undo={undo as () => void}
                            undoText={t('i18n.global.undo')}
                            close={() => setShowAlert(false)}
                            show={showAlert}
                        />
                        <ConfigSceneLoading.Component in={modelLoading} timeout={500} />
                        <Scene.Component
                            particleTree={furnitureTree}
                            attachmentSelections={attachmentSelections ?? {}}
                            onSelectAttachmentOption={(attachmentTag: string, value: string) => {
                                dispatch(
                                    ConfigurationActions.selectAttachmentOptionAction({
                                        attachmentTag,
                                        value,
                                    }),
                                );
                                selectAttachment(attachmentTag, value);
                            }}
                            setGenerateGltf={gltf => setGenerateGltfFn(gltf)}
                            setGenerateModelRequest={mr => setGenerateModelRequestFn(mr)}
                            setModelLoading={(loading: boolean) => setModelLoading(loading)}
                            setSceneReadyForSnapshot={(ready: boolean) =>
                                setSceneReadyForSnapshot(ready)
                            }
                            setSceneError={(error: boolean) => setSceneError(error)}
                            setSnapshot={setSnapshotFn}
                        />
                    </Box>
                    <Box
                        sx={{
                            flexGrow: 0,
                            flexShrink: 0,
                            width: { xs: '100%', md: '460px' },
                            height: { xs: '255px', md: '100%' },
                        }}
                    >
                        <Style.Component
                            onUpdateSelection={onUpdateSelection}
                            onUndo={
                                undo
                                    ? () => {
                                          dispatch(ConfigurationActions.undoAction());
                                          undo();
                                          setShowAlert(false);
                                      }
                                    : undefined
                            }
                            hideAlert={() => setShowAlert(false)}
                            onRedo={
                                redo
                                    ? () => {
                                          dispatch(ConfigurationActions.redoAction());
                                          redo();
                                      }
                                    : undefined
                            }
                            selections={propertySelections}
                            existingPropertyOptions={existingPropertyOptions}
                            compatiblePropertyOptions={compatiblePropertyOptions}
                            startWithSelections={startingSelections}
                            configuratorDefinition={configuratorDefinition}
                            addToCart={() => addToCart('finalise step')}
                            priceDeltas={priceDeltas}
                            requestPriceDelta={requestPriceDelta}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    ) : (
        <StartupScreen.Component
            logoUrl="assets/fixtuur-logo.svg"
            onClose={appControl.closeApp}
            onUnMount={() => dispatch(NavigationActions.initialLoadingFinishedAction({}))}
            statusText={{
                initial: t('i18n.configuration.initialLoading'),
                middle: t('i18n.configuration.middleLoading'),
                final: t('i18n.configuration.finalLoading'),
            }}
        />
    );
}
